import {
  Box,
  Button,
  Stack,
  styled,
  Typography,
  useTheme,
} from '@mui/material';
import { OndemandVideoOutlinedIcon } from 'assets/icons';
import { Card } from 'components/commons';
import SecuredImage from 'components/hooks/SecuredImage';
import { API_PATHS } from 'constant/api';
import { GENDER } from 'constant/enum';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  convertDateUTCToLocal,
  DatePattern,
  getKeyFromLocalStorage,
} from 'utils/constant';
import { getReferenceDetail, getShots } from 'utils/fetch';
import ja from 'utils/ja';

const TypographyCustom = styled(Typography)(() => ({
  fontWeight: '400',
  lineHeight: '48px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: '-webkit-box',
  WebkitLineClamp: '1',
  WebkitBoxOrient: 'vertical',
}));

const UserDetail = () => {
  const theme = useTheme();
  const { id } = useParams();
  const [searchParams] = useSearchParams();

  const appId = getKeyFromLocalStorage('appId');
  const appKey = getKeyFromLocalStorage('appKey');
  const dbId = getKeyFromLocalStorage('dbId');
  const dbKey = getKeyFromLocalStorage('dbKey');

  const [visitQuery] = useState<IVisitQuery>({
    startTime: searchParams.get('startTime') ?? '',
    endTime: searchParams.get('endTime') ?? '',
    referenceId: id ?? '',
  });
  const [tagsStatus, setTagsStatus] = useState<string[]>([]);
  const [userInfo, setUserInfo] = useState<IReferenceDetail>();
  const [faces, setFaces] = useState<IDataFace[]>([]);

  const handleGetDetail = async () => {
    try {
      const data = await getReferenceDetail(id ?? '', {
        appId: appId,
        appKey: appKey,
        dbId: dbId,
        dbKey: dbKey,
      });
      if (data) {
        setUserInfo({
          ...data,
          gender:
            data.gender === GENDER.MALE ? ja.common.male : ja.common.female,
        });
        if (data.tag) {
          setTagsStatus(data.tag.split(','));
        }
      }
    } catch (error) {
      //empty
    }
  };

  const handleShots = async (visitQuery: IVisitQuery) => {
    try {
      const { data } = await getShots(visitQuery, {
        appId: appId ?? '',
        appKey: appKey ?? '',
        dbId: dbId ?? '',
        dbKey: dbKey ?? '',
      });
      setFaces(
        data.map(
          (face: IFaceRes): IDataFace => ({
            id: face.id,
            camera: face.camera,
            timestamp: face.timestamp,
          })
        )
      );
    } catch (error) {
      // empty
    }
  };

  const handleToSafeView = () => {
    const timestamp = dayjs(faces[faces.length - 1].timestamp)
      .subtract(5, 'second')
      .valueOf();

    window.location.href = `https://safie.link/app/streaming/6PphGkKd5LmzcGJjNh3Q?timestamp=${timestamp}`;
  };

  useEffect(() => {
    handleGetDetail();
  }, []);

  useEffect(() => {
    if (visitQuery) {
      handleShots(visitQuery);
    }
  }, [visitQuery]);

  return (
    <Box
      sx={{
        flex: 1,
        height: { xs: 'calc(100dvh - 60px)', sm: 'calc(100dvh - 80px)' },
        mt: { xs: '60px', sm: '80px' },
        backgroundColor: '#F8F9FA',
        padding: '10px',
      }}
    >
      <Stack flexDirection='row' gap='10px'>
        <Box display='flex'>
          <SecuredImage
            src={`${API_PATHS.reference}/${userInfo?.id}/image`}
            alt='face'
            style={{
              width: 148,
              height: 148,
              objectFit: 'cover',
              border: '1px solid #000000',
            }}
          />
        </Box>
        <Stack sx={{ gap: '10px' }}>
          <Typography>
            {ja.user_detail.id}: {userInfo?.id.slice(-4)}
          </Typography>
          <Typography>
            {ja.user_detail.age}: {userInfo?.age}
          </Typography>
          <Typography>
            {ja.user_detail.gender}: {userInfo?.gender}
          </Typography>
          <Typography>
            {ja.user_detail.first_date}:{' '}
            <Typography component='span' sx={{ whiteSpace: 'nowrap' }}>
              {convertDateUTCToLocal(
                userInfo?.createdAt ?? '',
                DatePattern.DATE_TIME
              )}
            </Typography>
          </Typography>
        </Stack>
      </Stack>
      <Box
        sx={{
          my: '10px',
          width: '100%',
          display: 'flex',
          flexDirection: 'row',
          gap: '10px',
          overflowX: 'auto',
          pb: '6px',
        }}
      >
        {tagsStatus.map((tag, index) => (
          <Box
            key={tag + index}
            sx={{
              padding: '8px 16px',
              backgroundColor: theme.palette.primary.main,
              borderRadius: '16px',
            }}
          >
            <Typography
              component='p'
              sx={{
                minWidth: '98px',
                color: '#fff',
                textAlign: 'center',
              }}
            >
              {tag}
            </Typography>
          </Box>
        ))}
      </Box>
      <Button
        fullWidth
        sx={{
          height: '50px',
          padding: '4px',
          borderRadius: '4px',
          backgroundColor: '#0096b2',
          '&:hover': {
            backgroundColor: '#0096b2',
          },
          mb: '10px',
        }}
        onClick={handleToSafeView}
      >
        <OndemandVideoOutlinedIcon
          sx={{ color: '#fff', fontSize: '42px !important' }}
        />
      </Button>
      <Box
        sx={{
          height: 'calc(100vh - 345px)',
          overflowY: 'auto',
          mt: '10px',
          px: '6px',
        }}
      >
        <Box
          sx={{
            mb: '5px',
            display: 'grid',
            gridTemplateColumns: '0.7fr 1fr 2.3fr',
            gap: '10px',
            px: '5px',
            backgroundColor: '#fff',
            borderRadius: '4px',
          }}
        >
          {[
            ja.home.person_photo,
            ja.user_detail.location,
            ja.visit_detail.capture_time,
          ].map((title, index) => (
            <Typography
              key={`title-${index}`}
              component='h4'
              sx={{ lineHeight: '32px', fontWeight: '700' }}
            >
              {title}
            </Typography>
          ))}
        </Box>
        {faces.map((face) => (
          <Card
            key={face.id}
            fullWidth
            sx={{
              mb: '10px',
              gridTemplateColumns: '0.7fr 1fr 2.3fr',
            }}
          >
            <Box display='flex'>
              <SecuredImage
                src={`${API_PATHS.get_face_image(face.id)}`}
                alt='face'
                style={{
                  width: 48,
                  height: 48,
                  objectFit: 'cover',
                  border: '1px solid #000000',
                }}
              />
            </Box>
            <TypographyCustom>{face.camera}</TypographyCustom>
            <TypographyCustom>
              {convertDateUTCToLocal(face.timestamp, DatePattern.DATE_TIME)}
            </TypographyCustom>
          </Card>
        ))}
      </Box>
    </Box>
  );
};

export default UserDetail;
